// ═══════════════════════════════════════
// EDITOR — Contenteditable, highlight, curseur, getAbcContent
// ═══════════════════════════════════════

import { headerFormActive } from './state.js';

// ── Marqueurs privés (ignorés par abcjs) ─────────────────────────────────────
export const HEADER_SEPARATOR       = '%%SCOROU';
export const VOICE_HEADER_SEPARATOR = '%%SCOROU_HEADER_VOICE';
export const VOICE_SEPARATOR        = '%%SCOROU_VOICE';

const ALL_MARKERS = [HEADER_SEPARATOR, VOICE_HEADER_SEPARATOR, VOICE_SEPARATOR];
const MARKER_RE   = /^%%(SCOROU|SCOROU_HEADER_VOICE|SCOROU_VOICE)\s*$/m;

// ── Parseur de document ABC ───────────────────────────────────────────────────
export function parseAbcDocument(text) {
    const lines = text.split('\n');

    const MARK_SCOROU        = 1;
    const MARK_HEADER_VOICE  = 2;
    const MARK_VOICE         = 3;

    function lineType(l) {
        const t = l.trim();
        if (/^%%SCOROU_HEADER_VOICE(\s|$)/.test(t)) return MARK_HEADER_VOICE;
        if (/^%%SCOROU_VOICE(\s|$)/.test(t))        return MARK_VOICE;
        if (t === '%%SCOROU')                        return MARK_SCOROU;
        return 0;
    }

    let scorouLine           = -1;
    const headerVoiceLines   = [];
    const headerVoiceMarkers = [];
    const voiceLines         = [];

    for (let i = 0; i < lines.length; i++) {
        const t = lineType(lines[i]);
        if (t === MARK_SCOROU)       scorouLine = i;
        if (t === MARK_HEADER_VOICE) { headerVoiceLines.push(i); headerVoiceMarkers.push(lines[i].trim()); }
        if (t === MARK_VOICE)        voiceLines.push(i);
    }

    if (scorouLine === -1 && headerVoiceLines.length === 0 && voiceLines.length === 0) {
        return { global: '', voiceHeaders: [], voices: [], _legacy: true };
    }

    if (scorouLine !== -1 && headerVoiceLines.length === 0) {
        const global = lines.slice(0, scorouLine).join('\n');
        const rest   = lines.slice(scorouLine + 1);

        if (voiceLines.length === 0) {
            return { global, voiceHeaders: [_extractVoiceHeader(global)], voices: [rest.join('\n')], _legacy: true };
        }

        const firstVoiceIdx = voiceLines[0] - (scorouLine + 1);
        const voiceHeader   = rest.slice(0, firstVoiceIdx).join('\n');
        const voices = [];
        for (let i = 0; i < voiceLines.length; i++) {
            const from = voiceLines[i] - (scorouLine + 1) + 1;
            const to   = i + 1 < voiceLines.length ? voiceLines[i + 1] - (scorouLine + 1) : rest.length;
            voices.push(rest.slice(from, to).join('\n').replace(/^\n|\n$/g, ''));
        }
        return { global, voiceHeaders: [voiceHeader.replace(/^\n|\n$/g, '')], voices, _legacy: true };
    }

    const global = lines.slice(0, headerVoiceLines[0]).join('\n').replace(/\n$/, '');
    const voiceHeaders = [];
    for (let i = 0; i < headerVoiceLines.length; i++) {
        const from = headerVoiceLines[i] + 1;
        const to   = i + 1 < headerVoiceLines.length
            ? headerVoiceLines[i + 1]
            : scorouLine !== -1 ? scorouLine : voiceLines[0] ?? lines.length;
        voiceHeaders.push(lines.slice(from, to).join('\n').replace(/^\n|\n$/g, ''));
    }
    const voices = [];
    for (let i = 0; i < voiceLines.length; i++) {
        const from = voiceLines[i] + 1;
        const to   = i + 1 < voiceLines.length ? voiceLines[i + 1] : lines.length;
        voices.push(lines.slice(from, to).join('\n').replace(/^\n|\n$/g, ''));
    }
    return { global, voiceHeaders, voices };
}

function _extractVoiceHeader(headersText) {
    const lines = headersText.split('\n');
    let kIdx = -1;
    for (let i = 0; i < lines.length; i++) {
        if (/^K:/i.test(lines[i])) kIdx = i;
    }
    if (kIdx === -1) return '';
    let end = kIdx + 1;
    while (end < lines.length && /^(V:\d+|%%)/.test(lines[end])) end++;
    return lines.slice(kIdx, end).join('\n');
}

// ── Reconstruction ABC ────────────────────────────────────────────────────────

export function getAbcContent() {
    const global        = _getElText('abc-headers');
    const voiceHeaders  = _getVoiceHeaderTexts();
    const voices        = _getVoiceTexts();
    const instrumentIds = _getVoiceInstrumentIds();
    const cleanVoices   = voices.map(v => _stripMarkers(v).trim());
    return _buildAbcContent(global, voiceHeaders, cleanVoices, instrumentIds);
}

export function getAbcForRender() {
    return getAbcForRenderWithMap().abc;
}

/**
 * Comme getAbcForRender(), mais retourne aussi la correspondance entre les
 * lignes de l'ABC rendu et les lignes des éditeurs de voix.
 * @returns {{ abc: string, lineMap: Array<{voice:number, line:number, col0:number}|null> }}
 *   lineMap[n] = origine de la ligne n de abc (null pour les entêtes).
 *   col0 = nombre de caractères retirés en début de ligne par le trim().
 */
export function getAbcForRenderWithMap() {
    const global       = _getElText('abc-headers');
    const voiceHeaders = _getVoiceHeaderTexts();
    const rawVoices    = _getVoiceTexts();
    const voices       = rawVoices.map(v => _stripMarkers(v).trim());
    if (!global && voices.length === 0) return { abc: _getElText('abc'), lineMap: [] };

    const count = voiceHeaders.length;
    const keep  = [];
    for (let i = 0; i < count; i++) if (!isVoiceHidden(i)) keep.push(i);

    // Sécurité : si tout est masqué, on rend tout (jamais d'ABC sans corps)
    const kept = keep.length > 0 ? keep : Array.from({ length: count }, (_, i) => i);

    let abc, usedIdx;
    if (kept.length === count) {
        // Aucune voix masquée → chemin normal, aucune transformation
        abc     = _buildAbcForRender(global, voiceHeaders, voices);
        usedIdx = voices.map((_, i) => i);
    } else {
        const keptNums    = kept.map(i => i + 1);
        const keptVoices  = kept.map(i => voices[i]);
        const keptHeaders = _ensureKeyLine(kept.map(i => voiceHeaders[i]), voiceHeaders, keptVoices);
        abc     = _buildAbcForRender(_filterScoreDirective(global, keptNums), keptHeaders, keptVoices);
        usedIdx = kept;
    }

    return { abc, lineMap: _buildRenderLineMap(abc, usedIdx, rawVoices, voices) };
}

/**
 * Les corps de voix sont toujours en fin d'ABC rendu (voir _buildAbcForRender) :
 * on part de la fin pour savoir où commence chaque voix, puis on aligne ses
 * lignes sur celles de l'éditeur.
 */
function _buildRenderLineMap(abc, usedIdx, rawVoices, voices) {
    const total     = abc.split('\n').length;
    const lineMap   = new Array(total).fill(null);
    const bodyLines = usedIdx.reduce((n, i) => n + voices[i].split('\n').length, 0);

    let p = total - bodyLines;
    for (const i of usedIdx) {
        const rLines = voices[i].split('\n');
        const eLines = rawVoices[i].split('\n');
        _alignVoiceLines(rLines, eLines).forEach((m, r) => {
            if (m) lineMap[p + r] = { voice: i, line: m.line, col0: m.col0 };
        });
        p += rLines.length;
    }
    return lineMap;
}

/**
 * Aligne les lignes rendues (après stripMarkers + trim) sur les lignes de
 * l'éditeur. Seules des lignes vides / marqueurs ont pu disparaître, et seul
 * le début de la première ligne a pu être raccourci par le trim().
 */
function _alignVoiceLines(rLines, eLines) {
    const out = [];
    let j = 0;
    for (let r = 0; r < rLines.length; r++) {
        const target = rLines[r].trim();
        while (j < eLines.length && eLines[j].trim() !== target) j++;
        if (j >= eLines.length) { out.push(null); continue; }
        const e = eLines[j];
        out.push({ line: j, col0: r === 0 ? e.length - e.trimStart().length : 0 });
        j++;
    }
    return out;
}

/**
 * Retourne la sélection courante si elle est entièrement dans un éditeur de voix :
 * { voice, lineIdx, col, endLineIdx, endCol, collapsed } — sinon null.
 */
export function getVoiceSelection() {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return null;
    const range = sel.getRangeAt(0);
    const node  = range.startContainer.nodeType === Node.ELEMENT_NODE
        ? range.startContainer
        : range.startContainer.parentElement;
    const el = node?.closest('.abc-voice');
    const m  = el?.id.match(/^abc-voice-(\d+)$/);
    if (!m || !el.contains(range.endContainer)) return null;
    return { voice: parseInt(m[1], 10), ..._getCursorPos(el) };
}

/**
 * Sélectionne (lineIdx, col) → (endLineIdx, endCol) dans la voix voiceIdx :
 * ouvre la voix si elle est repliée, donne le focus à l'éditeur et fait
 * défiler pour afficher la ligne.
 */
export function selectInVoice(voiceIdx, lineIdx, col, endLineIdx = lineIdx, endCol = col) {
    const block = document.querySelector(`.voice-block[data-voice="${voiceIdx}"]`);
    const el    = document.getElementById(`abc-voice-${voiceIdx}`);
    if (!block || !el) return;

    // Voix repliée → on réutilise le bouton d'accordéon existant
    if (block.classList.contains('voice-closed')) block.querySelector('.voice-toggle')?.click();

    if (el.isContentEditable) el.focus({ preventScroll: true });

    const start = _pointFromPos(el, lineIdx, col);
    const end   = _pointFromPos(el, endLineIdx, endCol);
    if (!start || !end) return;

    const range = document.createRange();
    range.setStart(start.node, start.offset);
    range.setEnd(end.node, end.offset);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);

    // Fait défiler l'ascenseur de la voix (et du conteneur) si nécessaire
    start.div.scrollIntoView({ block: 'nearest' });
}

/**
 * Convertit (lineIdx, col) en point DOM { div, node, offset } dans el.
 */
function _pointFromPos(el, lineIdx, col) {
    const divs = [...el.children].filter(c => c.nodeName === 'DIV');
    const div  = divs[Math.min(lineIdx, divs.length - 1)];
    if (!div) return null;

    // Ligne vide (<br>)
    if (div.children.length === 1 && div.children[0].nodeName === 'BR') {
        return { div, node: div, offset: 0 };
    }

    let remaining = Math.max(0, col);
    const walker  = document.createTreeWalker(div, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
        if (remaining <= node.textContent.length) return { div, node, offset: remaining };
        remaining -= node.textContent.length;
    }
    // Fin du div
    return { div, node: div, offset: div.childNodes.length };
}

// ── Visibilité des voix (état de vue, non persisté dans l'ABC) ────────────────

export function isVoiceHidden(n) {
    const block = document.querySelector(`.voice-block[data-voice="${n}"]`);
    return block?.dataset.hidden === '1';
}

export function setVoiceHidden(n, hidden) {
    const block = document.querySelector(`.voice-block[data-voice="${n}"]`);
    if (!block) return;
    if (hidden) block.dataset.hidden = '1';
    else        delete block.dataset.hidden;
}

/**
 * Réécrit %%score pour ne conserver que les numéros de voix visibles.
 * Gère les tokens "N" et les groupes "(N N)" générés par _updateScoreDirective().
 * Un groupe réduit à un seul membre est dégroupé ; un %%score vide est supprimé.
 */
function _filterScoreDirective(global, keptNums) {
    if (!/^%%score\b/m.test(global)) return global;
    const set = new Set(keptNums.map(String));

    return global.replace(/^%%score\b(.*)$/m, (_line, rest) => {
        const tokens = rest.trim().match(/\([^)]*\)|\S+/g) ?? [];
        const out    = [];
        for (const tk of tokens) {
            if (tk.startsWith('(')) {
                const members = tk.slice(1, -1).trim().split(/\s+/).filter(v => set.has(v));
                if (members.length > 1)        out.push(`(${members.join(' ')})`);
                else if (members.length === 1) out.push(members[0]);
            } else if (tk === '|') {
                if (out.length) out.push(tk);
            } else if (set.has(tk)) {
                out.push(tk);
            }
        }
        while (out.length && out[out.length - 1] === '|') out.pop();
        return out.length ? `%%score ${out.join(' ')}` : '';
    }).replace(/\n{3,}/g, '\n\n');
}

/**
 * Garantit la présence d'une ligne K: quand la voix qui la portait est masquée.
 * On privilégie le [K:...] inline de la première voix visible (donc sa vraie
 * tonalité) et on retombe sur le K: de la voix masquée à défaut.
 * clef= et stafflines= sont retirés : chaque voix visible porte les siens
 * dans son propre [K:...] inline.
 */
function _ensureKeyLine(keptHeaders, allHeaders, keptVoices = []) {
    if (keptHeaders.some(h => /^K:/m.test(h))) return keptHeaders;

    const inline = (keptVoices[0] ?? '').match(/\[K:([^\]]*)\]/);
    const raw    = inline
        ? 'K:' + inline[1]
        : (allHeaders.find(h => /^K:/m.test(h))?.match(/^K:.*$/m)?.[0] ?? null);
    if (!raw) return keptHeaders;

    const kLine = raw
        .replace(/\s*\bclef=\S+/g, '')
        .replace(/\s*\bstafflines=\d+/g, '')
        .trimEnd();

    const out   = [...keptHeaders];
    const lines = (out[0] ?? '').split('\n');
    const vIdx  = lines.findIndex(l => /^V:\d+/i.test(l));
    if (vIdx !== -1) {
        lines.splice(vIdx + 1, 0, kLine);   // même structure que le header de la voix 0
        out[0] = lines.join('\n');
    } else {
        out.unshift(kLine);
    }
    return out;
}

export function getHeadersContent()      { return _getElText('abc-headers'); }
export function setHeadersContent(text)  { const el = document.getElementById('abc-headers'); if (el) el.textContent = _stripMarkers(text).trim(); }
export function getVoiceHeaderContent(n) { return _getElText(`abc-voice-header-${n}`); }
export function setVoiceHeaderContent(n, text) { const el = document.getElementById(`abc-voice-header-${n}`); if (el) el.textContent = _stripMarkers(text).trim(); }
export function getVoiceContent(n)       { return _getElText(`abc-voice-${n}`); }
export function setVoiceContent(n, text) { const el = document.getElementById(`abc-voice-${n}`); if (el) el.textContent = _stripMarkers(text).trim(); }
export function getVoiceCount()          { return document.querySelectorAll('.voice-block').length; }

// ── Highlight (syntaxe) ───────────────────────────────────────────────────────

const ABC_TOKENS = [
    { regex: /(\{[^}]*\})/g,               cls: 'abc-grace'   },
    { regex: /(![^!\s]+!)/g,               cls: 'abc-dynamic' },
    { regex: /(\|:|\:\||\:\:|\[\||\|\])/g, cls: 'abc-repeat'  },
    { regex: /(?<!\|)(\|)(?!\|)/g,         cls: 'abc-barline' },
    { regex: /^([A-Z]:)(.*)/gm,            cls: 'abc-header'  },
];

function _tokenizeLine(line) {
    let l = line.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    ABC_TOKENS.forEach(({ regex, cls }) => {
        l = l.replace(regex, m => `<span class="${cls}">${m}</span>`);
    });
    return l;
}

function _lineClass(raw) {
    if (/^\s*$/.test(raw))   return 'abc-line-empty';
    if (/^[A-Z]:/.test(raw)) return 'abc-line-header';
    if (/^%%/.test(raw))     return 'abc-line-directive';
    if (/^V:\d/.test(raw))   return 'abc-line-voice';
    return '';
}

/**
 * Highlight d'un élément .abc-voice.
 * Ne touche PAS au curseur — appelé uniquement depuis _applyEdit()
 * qui repositionne le curseur lui-même après.
 */
export function highlight(elOrId = 'abc-voice-0') {
    const el = typeof elOrId === 'string' ? document.getElementById(elOrId) : elOrId;
    if (!el) return;
    const text  = _readLines(el).join('\n');
    const clean = _stripMarkers(text);
    el.innerHTML = clean.split('\n').map(line => {
        const cls = 'abc-line' + (_lineClass(line) ? ' ' + _lineClass(line) : '');
        return `<div class="${cls}">${_tokenizeLine(line) || '<br>'}</div>`;
    }).join('');
}

export function highlightAll() {
    document.querySelectorAll('.abc-voice[contenteditable]').forEach(el => highlight(el));
}

// ── Lecture des lignes depuis le DOM ──────────────────────────────────────────

/**
 * Retourne le tableau de lignes texte d'un .abc-voice,
 * en lisant les <div> enfants directs (notre structure canonique).
 * Si pas encore de <div> (init ou textContent brut), split sur \n.
 */
function _readLines(el) {
    const divs = [...el.children].filter(c => c.nodeName === 'DIV');
    if (divs.length > 0) {
        return divs.map(d => {
            // <br> seul = ligne vide
            if (d.children.length === 1 && d.children[0].nodeName === 'BR' && d.textContent === '') return '';
            return d.textContent;
        });
    }
    // Fallback : pas encore de divs
    return el.textContent.split('\n');
}

// ── Gestion centralisée des éditions ─────────────────────────────────────────
//
// Principe : le .abc-voice est en lecture seule pour le navigateur.
// Toutes les modifications passent par _applyEdit(el, fn) :
//   fn reçoit { lines, lineIdx, col } et retourne { lines, lineIdx, col }
// Puis highlight() est rappelé et le curseur repositionné.

/**
 * Applique une modification au contenu de la voix et replace le curseur.
 * @param {HTMLElement} el     — élément .abc-voice
 * @param {Function}   editFn — (state) => newState
 *                              state = { lines: string[], lineIdx: number, col: number }
 */
export function applyEdit(el, editFn) {
    const pos   = _getCursorPos(el);
    const lines = _readLines(el).map(l => _stripMarkers(l));
    const next  = editFn({ lines, lineIdx: pos.lineIdx, col: pos.col, endLineIdx: pos.endLineIdx, endCol: pos.endCol, collapsed: pos.collapsed });
    el.innerHTML = next.lines.map(line => {
        const cls = 'abc-line' + (_lineClass(line) ? ' ' + _lineClass(line) : '');
        return `<div class="${cls}">${_tokenizeLine(line) || '<br>'}</div>`;
    }).join('');
    _setCursorPos(el, next.lineIdx, next.col);
}

// ── Position curseur (lineIdx, col) ──────────────────────────────────────────

/**
 * Retourne la position d'un point (container, offset) dans el.
 * { lineIdx, col }
 */
function _posFromPoint(el, container, offset) {
    const divs = [...el.children].filter(c => c.nodeName === 'DIV');
    for (let i = 0; i < divs.length; i++) {
        if (divs[i].contains(container) || divs[i] === container) {
            const r = document.createRange();
            r.selectNodeContents(divs[i]);
            r.setEnd(container, offset);
            return { lineIdx: i, col: r.toString().length };
        }
    }
    const last = divs.length - 1;
    return { lineIdx: Math.max(0, last), col: divs[last]?.textContent.length ?? 0 };
}

/**
 * Retourne { lineIdx, col, endLineIdx, endCol, collapsed }.
 */
function _getCursorPos(el) {
    const sel = window.getSelection();
    if (!sel.rangeCount) return { lineIdx: 0, col: 0, endLineIdx: 0, endCol: 0, collapsed: true };
    const range = sel.getRangeAt(0);
    const start = _posFromPoint(el, range.startContainer, range.startOffset);
    const end   = range.collapsed ? start : _posFromPoint(el, range.endContainer, range.endOffset);
    return { lineIdx: start.lineIdx, col: start.col, endLineIdx: end.lineIdx, endCol: end.col, collapsed: range.collapsed };
}

/**
 * Supprime la sélection dans lines et retourne { lines, lineIdx, col }.
 * À appeler quand !collapsed avant toute autre opération.
 */
export function deleteSelection({ lines, lineIdx, col, endLineIdx, endCol }) {
    const next = [...lines];
    if (lineIdx === endLineIdx) {
        next[lineIdx] = next[lineIdx].slice(0, col) + next[lineIdx].slice(endCol);
    } else {
        next[lineIdx] = next[lineIdx].slice(0, col) + next[endLineIdx].slice(endCol);
        next.splice(lineIdx + 1, endLineIdx - lineIdx);
    }
    return { lines: next, lineIdx, col };
}

/**
 * Place le curseur à { lineIdx, col } dans el.
 */
function _setCursorPos(el, lineIdx, col) {
    if (document.activeElement !== el) return;
    const divs = [...el.children].filter(c => c.nodeName === 'DIV');
    const div  = divs[Math.min(lineIdx, divs.length - 1)];
    if (!div) return;

    const sel   = window.getSelection();
    const range = document.createRange();

    // Cas ligne vide (<br>)
    if (div.children.length === 1 && div.children[0].nodeName === 'BR') {
        range.setStart(div, 0);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
        return;
    }

    // Parcourir les nœuds texte du div pour trouver la bonne position
    let remaining = Math.max(0, col);
    const walker  = document.createTreeWalker(div, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
        if (remaining <= node.textContent.length) {
            range.setStart(node, remaining);
            range.collapse(true);
            sel.removeAllRanges();
            sel.addRange(range);
            return;
        }
        remaining -= node.textContent.length;
    }
    // Fin du div
    range.selectNodeContents(div);
    range.collapse(false);
    sel.removeAllRanges();
    sel.addRange(range);
}

// ── Insertion de texte au curseur ────────────────────────────────────────────

export function getContextAtCursor(el) {
    const pos   = _getCursorPos(el);
    const lines = _readLines(el).map(l => _stripMarkers(l));
    const line  = lines[pos.lineIdx] ?? '';
    const before = lines.slice(0, pos.lineIdx).join('\n')
        + (pos.lineIdx > 0 ? '\n' : '')
        + line.slice(0, pos.col);
    const after  = line.slice(pos.col)
        + (pos.lineIdx < lines.length - 1 ? '\n' : '')
        + lines.slice(pos.lineIdx + 1).join('\n');
    const sel   = window.getSelection();
    const range = sel.rangeCount ? sel.getRangeAt(0).cloneRange() : null;
    return { before, after, range };
}

export function insertText(el, text) {
    applyEdit(el, (state) => {
        // Si sélection, la supprimer d'abord
        const base = state.collapsed ? state : deleteSelection(state);
        const next = [...base.lines];
        next[base.lineIdx] = next[base.lineIdx].slice(0, base.col) + text + next[base.lineIdx].slice(base.col);
        return { lines: next, lineIdx: base.lineIdx, col: base.col + text.length };
    });
}

/**
 * Insère du texte pouvant contenir des sauts de ligne, en remplaçant
 * la sélection courante le cas échéant. Curseur placé en fin d'insertion.
 */
export function insertMultilineText(el, text) {
    const clean = String(text).replace(/\r\n?/g, '\n');
    applyEdit(el, (state) => {
        const base   = state.collapsed ? state : deleteSelection(state);
        const lines  = [...base.lines];
        const cur    = lines[base.lineIdx] ?? '';
        const before = cur.slice(0, base.col);
        const after  = cur.slice(base.col);
        const parts  = clean.split('\n');

        if (parts.length === 1) {
            lines[base.lineIdx] = before + parts[0] + after;
            return { lines, lineIdx: base.lineIdx, col: base.col + parts[0].length };
        }

        const last = parts[parts.length - 1];
        lines.splice(
            base.lineIdx, 1,
            before + parts[0],
            ...parts.slice(1, -1),
            last + after
        );
        return { lines, lineIdx: base.lineIdx + parts.length - 1, col: last.length };
    });
}

// ── saveCursor / restoreCursor (compatibilité history.js) ────────────────────
// L'historique stocke un offset entier. On le convertit vers/depuis (lineIdx, col).

export function saveCursor(el) {
    const { lineIdx, col } = _getCursorPos(el);
    const lines = _readLines(el);
    // Offset = somme des longueurs des lignes précédentes + \n + col courant
    let offset = col;
    for (let i = 0; i < lineIdx; i++) offset += lines[i].length + 1;
    return offset;
}

export function restoreCursor(el, offset) {
    if (document.activeElement !== el) return;
    const lines = _readLines(el);
    let remaining = offset;
    for (let i = 0; i < lines.length; i++) {
        if (remaining <= lines[i].length) {
            _setCursorPos(el, i, remaining);
            return;
        }
        remaining -= lines[i].length + 1; // +1 pour le \n
    }
    // Au-delà → fin de dernière ligne
    const last = lines.length - 1;
    _setCursorPos(el, Math.max(0, last), lines[Math.max(0, last)]?.length ?? 0);
}

// ── Utilitaires internes ──────────────────────────────────────────────────────

function _getElText(id) {
    const el = document.getElementById(id);
    if (!el) return '';
    const lines = _readLines(el);
    return lines.join('\n');
}

function _getVoiceHeaderTexts() {
    return [...document.querySelectorAll('.voice-block')].map((_, i) => _getElText(`abc-voice-header-${i}`));
}

function _getVoiceInstrumentIds() {
    return [...document.querySelectorAll('.voice-block')]
        .map(el => el.dataset.instrumentId ? parseInt(el.dataset.instrumentId) : null);
}

function _getVoiceTexts() {
    return [...document.querySelectorAll('.voice-block')].map((_, i) => _getElText(`abc-voice-${i}`));
}

export function stripMarkers(text) {
    return text.replace(/^%%(SCOROU|SCOROU_HEADER_VOICE|SCOROU_VOICE)\s*$/gm, '')
               .replace(/\n{3,}/g, '\n\n');
}

function _stripMarkers(text) { return stripMarkers(text); }

function _buildAbcContent(global, voiceHeaders, voices, instrumentIds = []) {
    const parts = [global];
    voiceHeaders.forEach((h, i) => {
        const id  = instrumentIds[i];
        const sep = id != null ? `${VOICE_HEADER_SEPARATOR} instrument=${id}` : VOICE_HEADER_SEPARATOR;
        parts.push(sep);
        parts.push(h);
    });
    parts.push(HEADER_SEPARATOR);
    voices.forEach(v => { parts.push(VOICE_SEPARATOR); parts.push(v); });
    return parts.join('\n');
}

function _buildAbcForRender(global, voiceHeaders, voices) {
    const parts = [_stripMarkers(global)];
    voiceHeaders.forEach(h => parts.push(_stripMarkers(h)));
    voices.forEach(v => parts.push(v));
    return parts.join('\n');
}